import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { maintenanceService } from "@/features/maintenance/services/maintenanceService"

// Maintenance Issues
export function useMaintenanceIssues(property_id?: string) {
  return useQuery({
    queryKey: property_id ? ["maintenance-issues", { property_id }] : ["maintenance-issues"],
    queryFn: () => maintenanceService.listIssues(property_id),
  })
}

export function useMaintenanceIssue(id: string) {
  return useQuery({
    queryKey: ["maintenance-issues", id],
    queryFn: () => maintenanceService.getIssue(id),
    enabled: !!id,
  })
}

export function useCreateIssue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      maintenanceService.createIssue(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useUpdateIssue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      maintenanceService.updateIssue(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues"] })
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues", id] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useDeleteIssue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => maintenanceService.deleteIssue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useUploadMaintenancePhotos(issueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (files: File[]) => maintenanceService.uploadPhotos(issueId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues", issueId] })
    },
  })
}

export function useDeleteMaintenancePhoto(issueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (photoId: string) => maintenanceService.deletePhoto(issueId, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-issues", issueId] })
    },
  })
}

// Contractors
export function useContractors() {
  return useQuery({
    queryKey: ["contractors"],
    queryFn: () => maintenanceService.listContractors(),
  })
}

export function useCreateContractor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      maintenanceService.createContractor(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contractors"] })
    },
  })
}

export function useUpdateContractor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      maintenanceService.updateContractor(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["contractors"] })
      queryClient.invalidateQueries({ queryKey: ["contractors", id] })
    },
  })
}

export function useDeleteContractor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => maintenanceService.deleteContractor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contractors"] })
    },
  })
}
